// AuthContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { setupAuthStateListener, signOutUser } from '../lib/firebase';
import { checkCustomerExists } from '../api/auth';
import type { Customer, FirebaseUser } from '../types';
import api from "../api/axios";
// import { useLocation } from 'react-router-dom'; // optional

type AuthState = {
  isAuthenticated: boolean;
  customer: Customer | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
};

type AuthContextValue = AuthState & {
  updateCustomer: (customer: Customer, firebaseUser?: FirebaseUser) => void;
  clearAuth: () => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    customer: null,
    firebaseUser: null,
    isLoading: true,
  });

  // const unsubscribeRef = useRef<null | (() => void)>(null);
  const mountedRef = useRef(false);
  // const { pathname } = useLocation(); // optional

  const updateCustomer = useCallback((customer: Customer, firebaseUser?: FirebaseUser) => {
    setState(prev => ({
      ...prev,
      customer,
      firebaseUser: firebaseUser || prev.firebaseUser,
      isAuthenticated: true,
      isLoading: false,
    }));
  }, []);

  const clearAuth = useCallback(() => {
    delete api.defaults.headers.common.Authorization;
    setState({ isAuthenticated: false, customer: null, firebaseUser: null, isLoading: false });
  }, []);

  const signOut = useCallback(async () => {
    try { await signOutUser(); } finally { clearAuth(); }
  }, [clearAuth]);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    let unsub: (() => void) | null = null;
    // let cancelled = false;

    (async () => {
      try {
        // await the Promise<unsubscribe>
        unsub = await setupAuthStateListener(async (fbUser) => {
          if (!fbUser) {
            // logged out
            delete api.defaults.headers.common.Authorization;
            setState({ isAuthenticated: false, customer: null, firebaseUser: null, isLoading: false });
            return;
          }

          const currentPath = location.pathname.replace(/\/$/, '');
          if (currentPath === '/otp') {
            setState(prev => ({
              ...prev,
              isLoading: false,
              firebaseUser: {
                uid: fbUser.uid,
                email: fbUser.email || null,
                phoneNumber: fbUser.phoneNumber || null,
              },
            }));
            delete api.defaults.headers.common.Authorization;
            return;
          }

          const firebaseUser: FirebaseUser = {
            uid: fbUser.uid,
            email: fbUser.email || null,
            phoneNumber: fbUser.phoneNumber || null,
          };

          let shouldFetch = true;
          setState(prev => {
            if (prev.isAuthenticated) {
              shouldFetch = false;
              return { ...prev, firebaseUser, isLoading: false };
            }
            return { ...prev, firebaseUser, isLoading: true };
          });
          if (!shouldFetch) return;

          try {
            const token = await fbUser.getIdToken(true);
            api.defaults.headers.common.Authorization = `Bearer ${token}`;
            const res = await checkCustomerExists();
            setState(prev => ({
              ...prev,
              isAuthenticated: !!(res?.exists && res?.customer),
              customer: res?.customer ?? null,
              isLoading: false,
            }));
          } catch (e) {
            console.error(e);
            delete api.defaults.headers.common.Authorization;
            setState(prev => ({ ...prev, isLoading: false }));
          }
        });
      } catch (e) {
        console.error("[AuthProvider] Failed to attach listener", e);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    })();

    return () => {
      // cancelled = true;
      if (unsub) unsub();
    };
  }, [clearAuth]);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    updateCustomer,
    clearAuth,
    signOut,
  }), [state, updateCustomer, clearAuth, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};
